import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Avatar,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Container,
  Divider,
  FormControl,
  Grid,
  InputLabel,
  List,
  ListItemButton,
  ListItemText,
  MenuItem,
  Paper,
  Select,
  SelectChangeEvent,
  Snackbar,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import {
  Chat as ChatIcon,
  Refresh as RefreshIcon,
  Reply as ReplyIcon,
  Sync as SyncIcon,
  WhatsApp as WhatsAppIcon,
} from '@mui/icons-material';
import { useSearchParams } from 'react-router-dom';
import {
  WhatsAppConversation,
  WhatsAppMessage,
  WhatsAppWebNumber,
  listConversationMessages,
  listConversations,
  listNumbers,
  replyToConversation,
  syncConversation,
} from '../services/whatsappWebService';

const WhatsAppInbox: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [numbers, setNumbers] = useState<WhatsAppWebNumber[]>([]);
  const [conversations, setConversations] = useState<WhatsAppConversation[]>([]);
  const [selectedConversationId, setSelectedConversationId] = useState<number | null>(null);
  const [messages, setMessages] = useState<WhatsAppMessage[]>([]);
  const [loadingConversations, setLoadingConversations] = useState(false);
  const [loadingMessages, setLoadingMessages] = useState(false);
  const [syncingConversation, setSyncingConversation] = useState(false);
  const [replying, setReplying] = useState(false);
  const [replyText, setReplyText] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [senderFilter, setSenderFilter] = useState<number | ''>('');
  const [unreadOnly, setUnreadOnly] = useState<boolean>(false);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success' as 'success' | 'error' | 'info' | 'warning',
  });

  const showSnackbar = (message: string, severity: 'success' | 'error' | 'info' | 'warning') => {
    setSnackbar({ open: true, message, severity });
  };

  const selectedConversation = useMemo(
    () => conversations.find((conversation) => conversation.id === selectedConversationId) || null,
    [conversations, selectedConversationId]
  );
  const requestedConversationId = useMemo(() => {
    const rawValue = searchParams.get('conversationId');
    if (!rawValue) {
      return null;
    }
    const parsed = Number(rawValue);
    return Number.isNaN(parsed) ? null : parsed;
  }, [searchParams]);

  const selectedNumber = useMemo(
    () => numbers.find((number) => number.id === selectedConversation?.sender_number_id) || null,
    [numbers, selectedConversation]
  );

  const loadInboxData = useCallback(async () => {
    setLoadingConversations(true);
    try {
      const [numbersData, conversationsData] = await Promise.all([
        listNumbers(),
        listConversations({
          status: statusFilter === 'all' ? undefined : statusFilter,
          unread_only: unreadOnly || undefined,
          sender_number_id: senderFilter === '' ? undefined : Number(senderFilter),
          limit: 200,
        }),
      ]);

      setNumbers(numbersData);
      setConversations(conversationsData);

      if (conversationsData.length > 0) {
        setSelectedConversationId((prev) => {
          if (requestedConversationId && conversationsData.some((conversation) => conversation.id === requestedConversationId)) {
            return requestedConversationId;
          }
          const stillExists = conversationsData.some((conversation) => conversation.id === prev);
          return stillExists ? prev : conversationsData[0].id;
        });
      } else {
        setSelectedConversationId(null);
      }
    } catch (error: any) {
      showSnackbar(error.response?.data?.detail || 'Erro ao carregar inbox do WhatsApp', 'error');
    }
    setLoadingConversations(false);
  }, [requestedConversationId, senderFilter, statusFilter, unreadOnly]);

  const loadMessages = useCallback(async (conversationId: number) => {
    setLoadingMessages(true);
    try {
      const data = await listConversationMessages(conversationId, 200);
      setMessages(data);
    } catch (error: any) {
      showSnackbar(error.response?.data?.detail || 'Erro ao carregar mensagens da conversa', 'error');
    }
    setLoadingMessages(false);
  }, []);

  useEffect(() => {
    loadInboxData();
  }, [loadInboxData]);

  useEffect(() => {
    if (selectedConversationId) {
      loadMessages(selectedConversationId);
    } else {
      setMessages([]);
    }
  }, [selectedConversationId, loadMessages]);

  useEffect(() => {
    if (!requestedConversationId) {
      return;
    }
    if (selectedConversationId === requestedConversationId) {
      return;
    }
    if (conversations.some((conversation) => conversation.id === requestedConversationId)) {
      setSelectedConversationId(requestedConversationId);
    }
  }, [conversations, requestedConversationId, selectedConversationId]);

  useEffect(() => {
    const rawNumberId = searchParams.get('numberId');
    if (!rawNumberId) return;
    const parsed = Number(rawNumberId);
    if (!Number.isNaN(parsed)) {
      setSenderFilter(parsed as number | '');
    }
  }, [searchParams]);

  const handleSyncConversation = async () => {
    if (!selectedConversationId) {
      return;
    }

    setSyncingConversation(true);
    try {
      const result = await syncConversation(selectedConversationId, 80);
      showSnackbar(
        `Conversa sincronizada: ${result.scraped_count} mensagem(ns) lidas, ${result.inbound_messages} entrada(s)`,
        'success'
      );
      await loadInboxData();
      await loadMessages(selectedConversationId);
    } catch (error: any) {
      showSnackbar(error.response?.data?.detail || 'Erro ao sincronizar conversa', 'error');
    }
    setSyncingConversation(false);
  };

  const handleReply = async () => {
    if (!selectedConversationId || !replyText.trim()) {
      showSnackbar('Digite uma mensagem antes de responder', 'warning');
      return;
    }

    setReplying(true);
    try {
      await replyToConversation(selectedConversationId, replyText.trim());
      setReplyText('');
      showSnackbar('Resposta enviada com sucesso', 'success');
      await loadInboxData();
      await loadMessages(selectedConversationId);
    } catch (error: any) {
      showSnackbar(error.response?.data?.detail || 'Erro ao responder conversa', 'error');
    }
    setReplying(false);
  };

  const handleSelectConversation = (conversationId: number) => {
    setSelectedConversationId(conversationId);
    setSearchParams({ conversationId: String(conversationId) });
  };

  const getConversationStatusChip = (status: string) => {
    if (status === 'waiting_human') return <Chip label="Aguardando humano" color="warning" size="small" />;
    if (status === 'open') return <Chip label="Aberta" color="success" size="small" />;
    if (status === 'closed') return <Chip label="Fechada" color="default" size="small" />;
    return <Chip label={status} size="small" />;
  };

  const formatDate = (value?: string | null) => {
    if (!value) return '-';
    return new Date(value).toLocaleString('pt-BR');
  };

  const groupedStats = useMemo(() => {
    return {
      total: conversations.length,
      unread: conversations.reduce((acc, conversation) => acc + conversation.unread_count, 0),
      waitingHuman: conversations.filter((conversation) => conversation.status === 'waiting_human').length,
    };
  }, [conversations]);

  return (
    <Container maxWidth="xl">
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" component="h1" gutterBottom>
          Inbox WhatsApp
        </Typography>
        <Typography variant="subtitle1" color="text.secondary">
          Acompanhe conversas, sincronize mensagens do WhatsApp Web e responda leads em um só lugar.
        </Typography>
      </Box>

      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={4}>
          <Card>
            <CardContent>
              <Typography variant="body2" color="text.secondary">Conversas</Typography>
              <Typography variant="h4">{groupedStats.total}</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={4}>
          <Card>
            <CardContent>
              <Typography variant="body2" color="text.secondary">Mensagens não lidas</Typography>
              <Typography variant="h4">{groupedStats.unread}</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={4}>
          <Card>
            <CardContent>
              <Typography variant="body2" color="text.secondary">Aguardando humano</Typography>
              <Typography variant="h4">{groupedStats.waitingHuman}</Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      <Paper sx={{ p: 3, mb: 3 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={3}>
            <FormControl fullWidth size="small">
              <InputLabel id="status-filter-label">Status</InputLabel>
              <Select
                labelId="status-filter-label"
                label="Status"
                value={statusFilter}
                onChange={(event: SelectChangeEvent<string>) => setStatusFilter(event.target.value)}
              >
                <MenuItem value="all">Todos</MenuItem>
                <MenuItem value="open">Aberta</MenuItem>
                <MenuItem value="waiting_human">Aguardando humano</MenuItem>
                <MenuItem value="closed">Fechada</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={3}>
            <FormControl fullWidth size="small">
              <InputLabel id="sender-filter-label">Número</InputLabel>
              <Select
                labelId="sender-filter-label"
                label="Número"
                value={senderFilter}
                onChange={(event: SelectChangeEvent<number | string>) =>
                  setSenderFilter(event.target.value === '' ? '' : Number(event.target.value))
                }
              >
                <MenuItem value="">Todos</MenuItem>
                {numbers.map((number) => (
                  <MenuItem key={number.id} value={number.id}>
                    {number.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={3}>
            <Button
              variant={unreadOnly ? 'contained' : 'outlined'}
              startIcon={<WhatsAppIcon />}
              fullWidth
              onClick={() => setUnreadOnly((prev) => !prev)}
            >
              {unreadOnly ? 'Somente não lidas' : 'Mostrar todas'}
            </Button>
          </Grid>
          <Grid item xs={12} md={3}>
            <Button
              variant="outlined"
              startIcon={<RefreshIcon />}
              fullWidth
              onClick={loadInboxData}
              disabled={loadingConversations}
            >
              Atualizar inbox
            </Button>
          </Grid>
        </Grid>
      </Paper>

      <Grid container spacing={3}>
        <Grid item xs={12} md={4}>
          <Paper sx={{ height: 'calc(100vh - 320px)', minHeight: 540, overflow: 'hidden' }}>
            <Box sx={{ p: 2, borderBottom: '1px solid', borderColor: 'divider' }}>
              <Typography variant="h6">Conversas</Typography>
            </Box>
            {loadingConversations ? (
              <Box display="flex" justifyContent="center" py={6}>
                <CircularProgress />
              </Box>
            ) : (
              <List sx={{ p: 0, overflowY: 'auto', height: '100%' }}>
                {conversations.map((conversation) => {
                  const conversationNumber = numbers.find((number) => number.id === conversation.sender_number_id);
                  return (
                    <React.Fragment key={conversation.id}>
                      <ListItemButton
                        selected={selectedConversationId === conversation.id}
                        onClick={() => handleSelectConversation(conversation.id)}
                        alignItems="flex-start"
                        sx={{ py: 1.5 }}
                      >
                        <ListItemText
                          primary={
                            <Box display="flex" justifyContent="space-between" alignItems="center" gap={1}>
                              <Typography variant="body1" fontWeight={600} noWrap>
                                {conversation.remote_name || conversation.remote_phone}
                              </Typography>
                              {conversation.unread_count > 0 && (
                                <Chip label={conversation.unread_count} color="warning" size="small" />
                              )}
                            </Box>
                          }
                          secondary={
                            <Box>
                              <Typography variant="body2" color="text.secondary" noWrap>
                                {conversation.remote_phone}
                              </Typography>
                              <Box display="flex" alignItems="center" gap={1} mt={0.5} flexWrap="wrap">
                                {getConversationStatusChip(conversation.status)}
                                <Chip label={conversationNumber?.label || `Nº ${conversation.sender_number_id}`} size="small" variant="outlined" />
                              </Box>
                              <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
                                Última interação: {formatDate(conversation.last_message_at)}
                              </Typography>
                            </Box>
                          }
                        />
                      </ListItemButton>
                      <Divider />
                    </React.Fragment>
                  );
                })}
                {conversations.length === 0 && (
                  <Box textAlign="center" py={6}>
                    <Typography variant="body2" color="text.secondary">
                      Nenhuma conversa encontrada com os filtros atuais.
                    </Typography>
                  </Box>
                )}
              </List>
            )}
          </Paper>
        </Grid>

        <Grid item xs={12} md={8}>
          <Paper sx={{ minHeight: 540 }}>
            {!selectedConversation ? (
              <Box display="flex" justifyContent="center" alignItems="center" minHeight={540}>
                <Stack spacing={1} alignItems="center">
                  <ChatIcon color="disabled" sx={{ fontSize: 40 }} />
                  <Typography variant="body1" color="text.secondary">
                    Selecione uma conversa para ver o histórico.
                  </Typography>
                </Stack>
              </Box>
            ) : (
              <>
                <Box sx={{ p: 2.5, borderBottom: '1px solid', borderColor: 'divider' }}>
                  <Box display="flex" justifyContent="space-between" alignItems="flex-start" gap={2} flexWrap="wrap">
                    <Box>
                      <Typography variant="h6">
                        {selectedConversation.remote_name || selectedConversation.remote_phone}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {selectedConversation.remote_phone}
                      </Typography>
                      <Box display="flex" gap={1} mt={1} flexWrap="wrap">
                        {getConversationStatusChip(selectedConversation.status)}
                        <Chip
                          label={selectedNumber?.label || `Número ${selectedConversation.sender_number_id}`}
                          size="small"
                          variant="outlined"
                        />
                        {selectedConversation.campaign_id && (
                          <Chip label={`Campanha ${selectedConversation.campaign_id}`} size="small" variant="outlined" />
                        )}
                      </Box>
                    </Box>
                    <Stack direction="row" spacing={1}>
                      <Button
                        variant="outlined"
                        startIcon={syncingConversation ? <CircularProgress size={18} color="inherit" /> : <SyncIcon />}
                        onClick={handleSyncConversation}
                        disabled={syncingConversation}
                      >
                        Sincronizar
                      </Button>
                    </Stack>
                  </Box>
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                    Última mensagem: {formatDate(selectedConversation.last_message_at)}
                  </Typography>
                </Box>

                <Box sx={{ p: 2, backgroundColor: '#f8f9fb', height: 380, overflowY: 'auto' }}>
                  {loadingMessages ? (
                    <Box display="flex" justifyContent="center" py={6}>
                      <CircularProgress />
                    </Box>
                  ) : (
                    <Stack spacing={1.5}>
                      {messages.map((message) => {
                        const isOutbound = message.direction === 'outbound';
                        return (
                          <Box
                            key={message.id}
                            sx={{
                              display: 'flex',
                              justifyContent: isOutbound ? 'flex-end' : 'flex-start',
                            }}
                          >
                            <Box
                              sx={{
                                maxWidth: '75%',
                                px: 2,
                                py: 1.25,
                                borderRadius: 2,
                                backgroundColor: isOutbound ? 'primary.main' : 'common.white',
                                color: isOutbound ? 'primary.contrastText' : 'text.primary',
                                boxShadow: 1,
                              }}
                            >
                              <Box display="flex" alignItems="center" gap={1} mb={0.5}>
                                <Avatar
                                  sx={{
                                    width: 24,
                                    height: 24,
                                    bgcolor: isOutbound ? 'primary.dark' : 'grey.300',
                                    color: isOutbound ? 'primary.contrastText' : 'text.primary',
                                  }}
                                >
                                  {isOutbound ? <ReplyIcon sx={{ fontSize: 14 }} /> : <WhatsAppIcon sx={{ fontSize: 14 }} />}
                                </Avatar>
                                <Typography variant="caption" sx={{ opacity: 0.8 }}>
                                  {isOutbound ? 'Equipe' : 'Lead'}
                                </Typography>
                              </Box>
                              <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                                {message.content || '-'}
                              </Typography>
                              <Typography variant="caption" sx={{ display: 'block', mt: 0.75, opacity: 0.8 }}>
                                {formatDate(message.sent_at || message.received_at || message.created_at)}
                              </Typography>
                            </Box>
                          </Box>
                        );
                      })}
                      {messages.length === 0 && (
                        <Alert severity="info">
                          Ainda não há mensagens persistidas para esta conversa. Use "Sincronizar" para buscar o histórico no WhatsApp Web.
                        </Alert>
                      )}
                    </Stack>
                  )}
                </Box>

                <Box sx={{ p: 2, borderTop: '1px solid', borderColor: 'divider' }}>
                  <TextField
                    label="Responder lead"
                    multiline
                    minRows={3}
                    maxRows={6}
                    fullWidth
                    value={replyText}
                    onChange={(event) => setReplyText(event.target.value)}
                    placeholder="Digite aqui a resposta que será enviada pelo número conectado..."
                  />
                  <Box display="flex" justifyContent="space-between" alignItems="center" mt={2} gap={2} flexWrap="wrap">
                    <Typography variant="caption" color="text.secondary">
                      O envio será feito pelo número {selectedNumber?.label || selectedConversation.sender_number_id}.
                    </Typography>
                    <Button
                      variant="contained"
                      startIcon={replying ? <CircularProgress size={18} color="inherit" /> : <ReplyIcon />}
                      onClick={handleReply}
                      disabled={replying}
                    >
                      Enviar resposta
                    </Button>
                  </Box>
                </Box>
              </>
            )}
          </Paper>
        </Grid>
      </Grid>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
      >
        <Alert
          severity={snackbar.severity}
          onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default WhatsAppInbox;
